import { ReturnedAccessToken, UserEntity } from "@citychallenge/api-public";
import { generateRandomId } from "@citychallenge/common";
import {
  observable,
  action,
  reaction,
  computed,
  IReactionDisposer,
} from "mobx";
import { createContext } from "react";
import { ServiceInterface } from "../service.interface";
import { StorageService } from "../storage";
import SHA1 from "crypto-js/sha1";
import { Environment } from "../../environment";

const ACCESS_TOKEN_KEY = "access_token_persistence";
const DEVICE_TOKEN_KEY = "device_token_persistence";

export const AuthServiceContext = createContext<AuthService>(null as any);

export class AuthService implements ServiceInterface {
  public context = AuthServiceContext;

  @observable
  public serviceReady = false;

  constructor(private storageService: StorageService) {}

  private disposer?: IReactionDisposer;

  private migratedToSecure = this.storageService.getPreference(
    "MIGRATED_TO_SECURE",
    false
  );

  public async serviceWillMount() {
    this.disposer = reaction(
      () => this.currentAccessToken,
      () => this._persistAccessToken()
    );

    if (!(await this.migratedToSecure.ready$)) {
      // Not migrated to secure yet, migrate the access token now
      await this.storageService.migrateInsecureToSecure(ACCESS_TOKEN_KEY);
      this.migratedToSecure.setValue(true);
    }

    await this._initializeAccessToken();
  }

  public serviceWillUnmount() {
    if (this.disposer) this.disposer();
  }

  private async _initializeAccessToken() {
    const token = await this.storageService.getSecureItem(ACCESS_TOKEN_KEY);
    if (token) this.setAccessToken(JSON.parse(token));
  }

  private async _persistAccessToken() {
    if (this.currentAccessToken) {
      await this.storageService.setSecureItem(
        ACCESS_TOKEN_KEY,
        JSON.stringify(this.currentAccessToken)
      );
    } else {
      await this.storageService.removeSecureItem(ACCESS_TOKEN_KEY);
    }
  }

  @observable
  public currentAccessToken?: ReturnedAccessToken;

  @action
  public updateUserInfo(user: UserEntity) {
    if (!this.currentAccessToken) return;
    this.setAccessToken({
      ...this.currentAccessToken,
      user,
    });
  }

  @action
  public setAccessToken(accessToken?: ReturnedAccessToken) {
    this.currentAccessToken = accessToken;
    this.serviceReady = true;
  }

  @computed
  public get isLoggedIn() {
    return !!this.currentAccessToken;
  }

  @computed
  public get userId() {
    return this.currentAccessToken?.user.id;
  }

  private _deviceToken?: string;
  private _deviceSecret?: string;

  public getDeviceToken = async (): Promise<string> => {
    if (this._deviceToken) return this._deviceToken;

    const token = await this.storageService.getSecureItem(DEVICE_TOKEN_KEY);

    if (token) this._deviceToken = token;
    else {
      // Token doesn't exist yet, create one and save it to the secure storage
      const newToken = generateRandomId(undefined, 32);
      await this.storageService.setSecureItem(DEVICE_TOKEN_KEY, newToken);
      this._deviceToken = newToken;
    }

    return this._deviceToken;
  };

  public getDeviceSecret = async () => {
    if (!this._deviceSecret) {
      const token = await this.getDeviceToken();
      this._deviceSecret = SHA1(
        `${token}${Environment.identifier}` +
          `${Environment.version}${Environment.deviceId}` +
          // Salt to further obfuscate the algorithm
          `zZbgQsDMZ7XVnHFA`
      ).toString();
    }

    return this._deviceSecret;
  };
}
